import styled from "styled-components";
import theme from "theme";
import { newTabLinkProps } from "utils/newTabLinkProps";

export const Container = styled.div`
	max-width: 1280px;
	width: min(100%, 1280px);
	margin: 0 auto;
	padding: 0 ${theme.space.containerX};
	box-sizing: border-box;
`;

export const Section = styled.section`
	padding: ${theme.space.sectionY} 0;
	background: ${(p) => {
		if (p.$inverse) {
			return `linear-gradient(165deg, ${theme.color.darkL1} 0%, ${theme.color.dark} 52%, ${theme.color.darkL1} 100%)`;
		}
		if (p.$alt) return theme.color.surfaceMuted;
		return theme.color.surface;
	}};
	color: ${(p) => (p.$inverse ? theme.color.onDark : theme.color.dark)};
`;

export const SectionTitle = styled.h2`
	font-family: ${theme.fontFamily.sans};
	font-size: clamp(1.5rem, 3.5vw, 2.125rem);
	font-weight: 800;
	color: ${(p) => (p.$inverse ? theme.color.light : theme.color.dark)};
	margin: 0 0 0.5rem;
	letter-spacing: -0.03em;
	line-height: 1.15;
`;

export const SectionLead = styled.p`
	font-family: ${theme.fontFamily.sans};
	font-size: 1.0625rem;
	color: ${(p) => (p.$inverse ? theme.color.onDarkMuted : theme.color.greyD2)};
	max-width: 42rem;
	margin: 0 0 1.75rem;
	line-height: 1.6;
`;

/** Premium marketing card — use across homepage modules */
export const MarketingCard = styled.article`
	position: relative;
	border: 1px solid ${theme.color.border};
	border-radius: ${theme.radius.lg};
	background: ${theme.color.surface};
	box-shadow: ${theme.boxShadow.card};
	padding: ${theme.space.cardPad};
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	box-sizing: border-box;
	overflow: hidden;
	transition:
		border-color ${theme.transitionDuration.normal} ${theme.transitionTimingFunction.easeOut},
		box-shadow ${theme.transitionDuration.normal} ${theme.transitionTimingFunction.easeOut},
		transform ${theme.transitionDuration.fast} ${theme.transitionTimingFunction.easeOut};

	&::before {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 3px;
		background: linear-gradient(90deg, ${theme.color.primary}, ${theme.color.secondary});
		opacity: 0;
		transition: opacity ${theme.transitionDuration.normal} ${theme.transitionTimingFunction.easeOut};
	}

	&:hover {
		border-color: ${theme.color.borderStrong};
		box-shadow: ${theme.boxShadow.cardHover};
		transform: translateY(-2px);
	}

	&:hover::before {
		opacity: 1;
	}

	@media (prefers-reduced-motion: reduce) {
		transition: border-color 0.15s ease, box-shadow 0.15s ease;
		&:hover {
			transform: none;
		}
	}
`;

export const TrustChip = styled.span`
	display: inline-flex;
	align-items: center;
	font-family: ${theme.fontFamily.sans};
	font-size: 0.75rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.07em;
	color: ${theme.color.primary};
	background: ${theme.color.primaryMuted};
	border: 1px solid rgba(0, 119, 204, 0.2);
	padding: 0.35rem 0.65rem;
	border-radius: ${theme.radius.full};
`;

export const UnstyledLink = styled.a`
	color: inherit;
	text-decoration: none;
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
		border-radius: 2px;
	}
`;

export const TextLink = styled.a.attrs((p) => ({
	...newTabLinkProps(p.href),
}))`
	color: ${theme.color.primary};
	font-weight: 600;
	text-decoration: none;
	border-bottom: 1px solid transparent;
	&:hover {
		border-bottom-color: ${theme.color.primary};
		color: ${theme.color.primaryHover};
	}
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
		border-radius: 2px;
	}
`;

export const ButtonLink = styled.a.attrs((p) => ({
	...newTabLinkProps(p.href),
}))`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0.65rem 1.35rem;
	font-family: ${theme.fontFamily.sans};
	font-size: 0.9375rem;
	font-weight: 600;
	border-radius: ${theme.radius.sm};
	text-decoration: none;
	cursor: pointer;
	transition:
		background ${theme.transitionDuration.fast} ${theme.transitionTimingFunction.easeOut},
		color ${theme.transitionDuration.fast} ${theme.transitionTimingFunction.easeOut},
		border-color ${theme.transitionDuration.fast} ${theme.transitionTimingFunction.easeOut},
		box-shadow ${theme.transitionDuration.fast} ${theme.transitionTimingFunction.easeOut};
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
	}
`;

export const primaryButton = `
	background: ${theme.color.primary};
	color: #fff;
	border: 1px solid ${theme.color.primary};
	box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
	&:hover {
		background: ${theme.color.primaryHover};
		border-color: ${theme.color.primaryHover};
		color: #fff;
		box-shadow: 0 4px 12px rgba(0, 119, 204, 0.25);
	}
`;

export const secondaryButton = `
	background: ${theme.color.surface};
	color: ${theme.color.dark};
	border: 1px solid ${theme.color.border};
	&:hover {
		border-color: ${theme.color.borderStrong};
		background: ${theme.color.surfaceMuted};
	}
`;

export const PrimaryButton = styled(ButtonLink)`
	${primaryButton}
`;

export const SecondaryButton = styled(ButtonLink)`
	${secondaryButton}
`;
